import { DOCUMENT, SINGLETON } from './schemas';
import {
  AUDIENCE_DOCUMENT_IDS,
  SITE_IDS,
  type AudienceValue,
  type SiteId,
} from './audience';

export type DocumentTypeRegistryEntry = {
  /** Studio workspace(s) / frontend site(s) where this type belongs */
  workspaces: SiteId[];
  /** Already placed in the desk tree — hide from the root orphan pane */
  structured: boolean;
  /**
   * Audience to assume when a document has no `audience` field yet.
   * Business-only types default to business even without this flag.
   */
  defaultAudience?: AudienceValue;
};

const WWW: SiteId[] = [SITE_IDS.WWW];
const BUSINESS: SiteId[] = [SITE_IDS.BUSINESS];
const BOTH: SiteId[] = [SITE_IDS.WWW, SITE_IDS.BUSINESS];

/**
 * Single source of truth for document type ↔ workspace mapping.
 *
 * When adding a document type:
 * 1. Register it here with the correct workspace(s)
 * 2. Set structured: true once it appears in the desk structure (regions, site config, etc.)
 * 3. Unregistered types appear in the WWW workspace orphan pane (failsafe for new schemas)
 */
export const DOCUMENT_TYPE_REGISTRY: Record<string, DocumentTypeRegistryEntry> = {
  // — Site config. SEO defaults exist once per audience (www id and …-business). —
  [SINGLETON.CONFIG_SEO]: { workspaces: BOTH, structured: true },
  [SINGLETON.CONFIG_APPSTORES]: { workspaces: WWW, structured: true },
  [SINGLETON.CONFIG_PLACEHOLDER_IMAGES]: { workspaces: WWW, structured: true },
  [SINGLETON.CONFIG_SOCIAL]: { workspaces: WWW, structured: true },
  [DOCUMENT.REDIRECTS]: { workspaces: WWW, structured: true },
  [DOCUMENT.CONFIG_REUSABLE_BLOCKS]: { workspaces: WWW, structured: true },
  [DOCUMENT.FRAGMENT_REPORTS]: { workspaces: BOTH, structured: true, defaultAudience: SITE_IDS.WWW },

  // — WWW-only regional / marketing content —
  [DOCUMENT.INITIATIVE_PAGES]: { workspaces: WWW, structured: true },
  [DOCUMENT.ACQUISITION_B2C_PAGES]: { workspaces: WWW, structured: true },
  [DOCUMENT.FRAGMENT_SUPPORT_PATH]: { workspaces: WWW, structured: true },
  [DOCUMENT.FRAGMENT_SIGNATURE_GENERATOR]: { workspaces: WWW, structured: true },

  // — Business-only regional / marketing content —
  [DOCUMENT.ACQUISITION_B2B_PAGES]: { workspaces: BUSINESS, structured: true },
  [DOCUMENT.INITIATIVE_B2B_PAGES]: { workspaces: BUSINESS, structured: true },
  [DOCUMENT.BUSINESS_HOMEPAGE]: { workspaces: BUSINESS, structured: true },
  [DOCUMENT.FRAGMENT_FORMS]: { workspaces: BOTH, structured: true },
  [DOCUMENT.FRAGMENT_SALESFORCE_FORMS]: { workspaces: BUSINESS, structured: true },
  // Legacy Business Solutions — remain editable in www when the flag is off.
  [DOCUMENT.BUSINESS_SOLUTIONS_SOLUTIONS]: { workspaces: BUSINESS, structured: true },
  [DOCUMENT.BUSINESS_SOLUTIONS_MARKETPLACES]: { workspaces: BUSINESS, structured: true },
  [DOCUMENT.MARKETPLACE_PAGES]: { workspaces: BUSINESS, structured: true },
  [DOCUMENT.PARCELS_PAGES]: { workspaces: BUSINESS, structured: true },
  [DOCUMENT.PLATFORM_PAGES]: { workspaces: BUSINESS, structured: true },
  [DOCUMENT.DATE_LABELLING_PAGES]: { workspaces: BUSINESS, structured: true },
  [DOCUMENT.DONATIONS_PAGES]: { workspaces: BUSINESS, structured: true },
  [DOCUMENT.MARKETPLACES_VENDOR_TYPE]: { workspaces: BUSINESS, structured: true },

  // — Shared top-level content (both workspaces) —
  [DOCUMENT.AUTHORS]: { workspaces: BOTH, structured: true },
  [DOCUMENT.FAQ]: { workspaces: BOTH, structured: true },
  [DOCUMENT.STATISTICS]: { workspaces: BOTH, structured: true },
  [DOCUMENT.PEOPLE]: { workspaces: BOTH, structured: true },
  [DOCUMENT.PARTNER_LOGO]: { workspaces: BOTH, structured: true },
  [DOCUMENT.COMPANIES]: { workspaces: BOTH, structured: true },
  // System taxonomy — seeded docs only; not shown in desk
  [DOCUMENT.AUDIENCE]: { workspaces: BOTH, structured: true },

  // — Shared regional content (both workspaces) —
  [SINGLETON.THEME]: { workspaces: BOTH, structured: true },
  [DOCUMENT.PAGES]: { workspaces: WWW, structured: true },
  [DOCUMENT.LEGAL_PAGES]: { workspaces: WWW, structured: true },
  [DOCUMENT.CAREER_PAGES]: { workspaces: WWW, structured: true },
  [DOCUMENT.NAVIGATION_HEADERS]: { workspaces: BOTH, structured: true },
  [DOCUMENT.NAVIGATION_FOOTERS]: { workspaces: BOTH, structured: true },
  [DOCUMENT.ANNOUNCEMENTS]: { workspaces: BOTH, structured: true },
  [DOCUMENT.DEFAULT_BLOCKS]: { workspaces: BOTH, structured: true },

  // — WWW-only regional content —
  [DOCUMENT.SURPRISE_BAG_PAGES]: { workspaces: WWW, structured: true },
  [DOCUMENT.SURPRISE_BAG_HOME]: { workspaces: WWW, structured: true },
  [DOCUMENT.ABOUT_FOOD_WASTE_PAGES]: { workspaces: WWW, structured: true },
  [DOCUMENT.ABOUT_FOOD_WASTE_BLOG]: { workspaces: WWW, structured: true },
  [DOCUMENT.ABOUT_FOOD_WASTE_BLOG_CATEGORIES]: { workspaces: WWW, structured: true },
  [DOCUMENT.ABOUT_FOOD_WASTE_RESOURCES]: { workspaces: WWW, structured: true },
  [DOCUMENT.ABOUT_FOOD_WASTE_RESOURCES_CATEGORIES]: { workspaces: WWW, structured: true },
  [DOCUMENT.ABOUT_US_PAGES]: { workspaces: WWW, structured: true },
  [DOCUMENT.MEDIA_TAG]: { workspaces: WWW, structured: true },
  [DOCUMENT.PRESS_RELEASES]: { workspaces: WWW, structured: true },
  [DOCUMENT.PRESS_MEDIA_ASSETS]: { workspaces: WWW, structured: true },
  [DOCUMENT.PRESS_MEDIA_ASSETS_CATEGORIES]: { workspaces: WWW, structured: true },
  [DOCUMENT.PRESS_NEWS_COVERAGE]: { workspaces: WWW, structured: true },
  [DOCUMENT.FRAGMENT_SURPRISE_BAG]: { workspaces: WWW, structured: true },
  [DOCUMENT.FRAGMENT_BRAND_PROFILE_DEFAULTS]: { workspaces: WWW, structured: true },
  [DOCUMENT.BRAND_PROFILE]: { workspaces: WWW, structured: true },
};

export function getDocumentTypeRegistryEntry(typeId: string): DocumentTypeRegistryEntry | undefined {
  return DOCUMENT_TYPE_REGISTRY[typeId];
}

export function belongsToWorkspace(typeId: string, workspace: SiteId): boolean {
  const entry = getDocumentTypeRegistryEntry(typeId);
  if (!entry) {
    return workspace === SITE_IDS.WWW;
  }

  return entry.workspaces.includes(workspace);
}

/** True when at least one document type in a desk section belongs to the workspace */
export function isAnyDocumentTypeInWorkspace(
  documentTypes: string[],
  workspace: SiteId,
): boolean {
  return documentTypes.some(typeId => belongsToWorkspace(typeId, workspace));
}

/** Types owned exclusively by the Business workspace (not WWW) */
export function isBusinessDocumentType(typeId: string): boolean {
  const entry = getDocumentTypeRegistryEntry(typeId);
  return entry?.workspaces.includes(SITE_IDS.BUSINESS) === true
    && entry.workspaces.includes(SITE_IDS.WWW) === false;
}

/**
 * Audience taxonomy id to assume when a document has no `audience` field.
 * - explicit `defaultAudience` on the registry entry
 * - business-only workspace types → business
 * - otherwise www (consumer default / untagged legacy)
 */
export function getInferredAudienceDocumentId(typeId: string | null | undefined): string {
  if (!typeId) {
    return '';
  }

  const entry = getDocumentTypeRegistryEntry(typeId);
  if (entry?.defaultAudience) {
    return AUDIENCE_DOCUMENT_IDS[entry.defaultAudience];
  }
  if (isBusinessDocumentType(typeId)) {
    return AUDIENCE_DOCUMENT_IDS[SITE_IDS.BUSINESS];
  }
  return AUDIENCE_DOCUMENT_IDS[SITE_IDS.WWW];
}

/** @deprecated Use isBusinessDocumentType or the registry directly */
export const BUSINESS_DOCUMENT_TYPES: readonly string[] = Object.entries(DOCUMENT_TYPE_REGISTRY)
  .filter(([, entry]) => (
    entry.workspaces.includes(SITE_IDS.BUSINESS) && !entry.workspaces.includes(SITE_IDS.WWW)
  ))
  .map(([typeId]) => typeId);

/**
 * Whether an unplaced document type should appear in the workspace root orphan pane.
 * New/unregistered types only appear in WWW (failsafe).
 */
export function shouldShowOrphanDocumentType(typeId: string, workspace: SiteId): boolean {
  const entry = getDocumentTypeRegistryEntry(typeId);

  if (!entry) {
    return workspace === SITE_IDS.WWW;
  }

  if (entry.structured) {
    return false;
  }

  return belongsToWorkspace(typeId, workspace);
}
